/**
 * Settings → AI theme editor — ONE primary button, "Turn on AI editor"
 * ("Open AI editor" once it is set up), with the connect code under it.
 *
 *   status    GET /api/theme-editor-admin/status (?themeId= for a picked
 *             theme) → { enabled, connection: { state, themeName, lastUsedAt… },
 *             embed, newTabUrl, session limits, the current connect code }
 *   turn on   lib/themeEditorTurnOn.turnOnAiEditor({ themeId, prefetched })
 *             — inside the click: copy the code, open a NEW TAB at the
 *             theme editor (activateAppId only while the embed is off),
 *             POST /turn-on (ai_editor_enabled = true), toast "Code copied
 *             — if the editor asks, just paste it." The panel in the editor
 *             connects by itself (remembered browser → ticket → link
 *             code) and otherwise shows a paste box for this code.
 *   code      always visible while the AI editor is on: large monospace,
 *             copy icon ("Copy" → "Copied ✓"), "New code in 9:42". Codes
 *             are one-time and valid 10 minutes; the card fetches the next
 *             one when this one runs out. Minting never disconnects anyone.
 *   states    off        → "Turn on AI editor"
 *             almost     → "Almost done — click Save in the theme editor" + "Open AI editor"
 *             connected  → "Connected on {theme name}", last used, "Open AI editor", "Disconnect"
 *   themes    picker defaulting to the live theme; other themes (drafts
 *             such as a "Try theme") in a small dropdown.
 *   disconnect POST /sessions/revoke → every panel session and every
 *             remembered browser dies; the AI editor is switched off until
 *             "Turn on" is clicked again.
 *
 * Rendered in Settings (full card) and in the Dashboard capability modal
 * (compact). Honest copy: the panel lives inside the editor's preview
 * because Shopify doesn't allow apps inside the editor's sidebar.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Banner, BlockStack, Button, InlineStack, Select, Spinner, Text, Tooltip } from '@shopify/polaris';
import { apiFetch } from '../lib/api';
import { OPEN_LABEL, TURN_ON_LABEL, copyText, countdownText, relativeTime, turnOnAiEditor } from '../lib/themeEditorTurnOn';
import { openPlansModal } from '../utils/plansModal';

const BRAND = '#6B4EE8';
const CODE_FONT = 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';

/** Off / Almost done / Connected for a connection state. */
function StateBadge({ state }) {
  if (state === 'connected') return <Badge tone="success">Connected</Badge>;
  if (state === 'almost') return <Badge tone="attention">Almost done</Badge>;
  return <Badge>Off</Badge>;
}

/** "New code in 9:42" — calls onExpired once when the code runs out (the card then fetches the next one). */
function Countdown({ expiresAt, onExpired }) {
  const [text, setText] = useState(() => countdownText(expiresAt));
  useEffect(() => {
    setText(countdownText(expiresAt));
    const timer = setInterval(() => {
      const t = countdownText(expiresAt);
      setText(t);
      if (Date.parse(expiresAt) - Date.now() <= 0) { clearInterval(timer); onExpired?.(); }
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiresAt]);
  if (!text) return null;
  return <Text as="span" tone="subdued" variant="bodySm"><span data-testid="theme-editor-code-countdown">New code in {text}</span></Text>;
}

/**
 * The connect code under the button: large monospace, a copy icon
 * ("Copy" → "Copied ✓"), the countdown. Shown while the AI editor is on.
 */
function ConnectCode({ code, onExpired, onCopied }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => { setCopied(false); }, [code?.code]);
  if (!code?.code) return null;
  const display = code.display || (code.code.length === 8 ? `${code.code.slice(0, 4)}-${code.code.slice(4)}` : code.code);
  const copy = async () => {
    const ok = await copyText(code.code);
    setCopied(!!ok); onCopied?.(!!ok);
    if (ok) setTimeout(() => setCopied(false), 2500);
  };
  return (
    <div data-testid="theme-editor-code-row" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <InlineStack gap="200" blockAlign="center" wrap={false}>
        <span data-testid="theme-editor-ai-code" aria-label={`Connect code ${display}`} style={{ fontFamily: CODE_FONT, fontSize: 30, fontWeight: 700, letterSpacing: 3, color: '#1a1a2e', lineHeight: 1.1 }}>{display}</span>
        <Tooltip content={copied ? 'Copied ✓' : 'Copy'} dismissOnMouseOut>
          <button type="button" onClick={copy} aria-label={copied ? 'Copied' : 'Copy code'} data-testid="theme-editor-code-copy"
            style={{ border: '1px solid #e4e0f7', background: copied ? '#e9f9ef' : '#fff', color: copied ? '#0f6e3d' : BRAND, borderRadius: 8, width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            {copied ? (
              <svg width="16" height="16" viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10.5l4 4 8-9" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 20 20" aria-hidden="true"><rect x="7" y="7" width="10" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M13 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" fill="none" stroke="currentColor" strokeWidth="1.8" /></svg>
            )}
          </button>
        </Tooltip>
      </InlineStack>
      <Countdown expiresAt={code.expiresAt} onExpired={onExpired} />
    </div>
  );
}

/**
 * Part 6 — "Autonoma sections" library row: the reusable section files
 * (installed once per theme, versioned + checksummed). Files the merchant
 * edited by hand are never overwritten silently: they are listed here
 * with an "Update anyway" that rewrites them (backups come back).
 */
function SectionLibraryRow() {
  const [lib, setLib] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = useCallback(async () => {
    try {
      const res = await apiFetch('/api/theme-editor-admin/section-library', { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setLib(data);
    } catch { /* informational row */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!lib || (!lib.installed && !lib.handEdited?.length && !lib.outdated?.length)) return null;
  const stale = [...(lib.handEdited || []), ...(lib.outdated || [])];
  const update = async (keys) => {
    setBusy(true); setMsg(null);
    try {
      const res = await apiFetch('/api/theme-editor-admin/section-library/update', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ keys }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || 'Update failed');
      setMsg(`Updated ${data.library?.written?.length || 0} file(s) to v${data.library?.version}.`);
      await load();
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  return (
    <div data-testid="section-library-row" style={{ border: '1px solid #e4e0f7', borderRadius: 12, padding: '14px 16px', background: '#fbfaff' }}>
      <BlockStack gap="200">
        <InlineStack gap="200" blockAlign="center" wrap>
          <Text as="h3" variant="headingSm">Autonoma sections in your theme</Text>
          {lib.upToDate ? <Badge tone="success">v{lib.version} · up to date</Badge> : <Badge tone="attention">Needs attention</Badge>}
        </InlineStack>
        <Text as="p" tone="subdued" variant="bodySm">
          Generated pages are built from these reusable sections (hero, trust bar, steps, reviews, FAQ…). They appear under
          “Add section” in the theme editor, so you can add more, reorder or delete them like any other section.
        </Text>
        {lib.handEdited?.length > 0 && (
          <Banner tone="warning" title={`${lib.handEdited.length} section file${lib.handEdited.length === 1 ? ' was' : 's were'} edited by hand`}>
            <p>
              Autonoma won’t overwrite {lib.handEdited.map(h => h.key.replace(/^sections\/|^assets\/|^snippets\//, '')).join(', ')} because the code differs from what it installed.
              Updating replaces your edits with the current Autonoma version (your version is kept as a backup in the response).
            </p>
          </Banner>
        )}
        {stale.length > 0 && (
          <InlineStack gap="200" blockAlign="center" wrap>
            <Button onClick={() => update(stale.map(s => s.key))} disabled={busy} data-testid="section-library-update">
              {busy ? 'Updating…' : `Update ${stale.length} file${stale.length === 1 ? '' : 's'} anyway`}
            </Button>
            <Button variant="plain" onClick={load} disabled={busy}>Re-check</Button>
          </InlineStack>
        )}
        {msg && <Text as="p" tone="subdued" variant="bodySm">{msg}</Text>}
      </BlockStack>
    </div>
  );
}

/** The card's state for a theme: off (switch is off) | almost (on, embed not saved) | connected. */
function stateFor(enabled, embed) {
  if (!enabled) return 'off';
  return embed?.enabled ? 'connected' : 'almost';
}

export default function ThemeEditorAiCard({ compact = false }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [locked, setLocked] = useState(false);
  const [busy, setBusy] = useState(null); // 'turn-on' | 'open' | 'disconnect' | null
  const [info, setInfo] = useState(null);

  // Theme picker: the live theme by default, the others (drafts, "Try theme") in the dropdown.
  const [themes, setThemes] = useState(null);
  const [pickedId, setPickedId] = useState('');
  const [picked, setPicked] = useState(null); // GET /status?themeId= → { themeId, embed, … } for a non-live pick
  const [pickedLoading, setPickedLoading] = useState(false);
  const pickSeq = useRef(0);

  // The connect code — { code, display, expiresAt }. Always current while the
  // AI editor is on: /status mints one when none is active, and the card
  // fetches the next one the moment this one expires.
  const [code, setCode] = useState(null);
  const [minting, setMinting] = useState(false);
  const statusRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch('/api/theme-editor-admin/status', { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || 'Could not load the theme editor status.');
      statusRef.current = data;
      setStatus(data);
      if (data.pairing?.code) setCode({ code: data.pairing.code, display: data.pairing.display, expiresAt: data.pairing.expiresAt });
      if (data.aiChat && data.aiChat.allowed === false) setLocked(true);
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  /** GET /pairing-code?mint=1 — the current code, or a new one when the last ran out. Never disconnects anyone. */
  const fetchCode = useCallback(async () => {
    if (minting) return;
    setMinting(true);
    try {
      const res = await apiFetch('/api/theme-editor-admin/pairing-code?mint=1', { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403 && data.error === 'feature_not_in_plan') { setLocked(true); return; }
      if (!res.ok) throw new Error(data.message || data.error || 'Could not get a connect code.');
      if (data.code) setCode({ code: data.code, display: data.display, expiresAt: data.expiresAt });
    } catch (e) { setError(e.message); } finally { setMinting(false); }
  }, [minting]);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/theme-editor-admin/themes', { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || data.error || 'Could not load your themes.');
        setThemes(data.themes || []);
      } catch { setThemes([]); }
    })();
  }, []);

  const liveTheme = (themes || []).find(t => t.live) || null;
  const liveId = liveTheme ? String(liveTheme.id) : (status?.themeId ? String(status.themeId) : '');
  const targetId = pickedId || liveId;
  const isLivePick = !pickedId || pickedId === liveId;
  const targetTheme = (themes || []).find(t => String(t.id) === String(targetId)) || null;
  const targetName = targetTheme?.name || status?.connection?.themeName || null;

  const pickTheme = async (id) => {
    setPickedId(id); setPicked(null); setInfo(null);
    if (!id || id === liveId) return;
    const seq = ++pickSeq.current; // a slower earlier pick must not overwrite a later one
    setPickedLoading(true);
    try {
      const res = await apiFetch(`/api/theme-editor-admin/status?themeId=${encodeURIComponent(id)}`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (seq !== pickSeq.current) return;
      if (!res.ok) throw new Error(data.message || data.error || 'Could not check that theme.');
      setPicked(data.theme || null);
    } catch (e) { setError(e.message); } finally { if (seq === pickSeq.current) setPickedLoading(false); }
  };

  const enabled = !!status?.enabled;
  const embed = isLivePick ? status?.embed : (picked?.embed ?? null);

  // Switched on but no code in hand (first load without one, or the last
  // one just ran out): fetch the next one — once per gap.
  const codeGap = useRef(false);
  useEffect(() => {
    if (enabled && !code && !minting && !codeGap.current) { codeGap.current = true; fetchCode(); }
    if (code) codeGap.current = false;
  }, [enabled, code, minting, fetchCode]);
  const state = stateFor(enabled, embed);
  const lastUsed = status?.connection?.lastUsedAt || status?.connection?.lastConnectedAt || null;

  // The one flow (shared with the dashboard nudge and the bell): inside the
  // click — copy the code + open the editor in a new tab — then switch on.
  // `prefetched` carries the target theme's new-tab URL and the code so
  // the synchronous part has what it needs.
  const turnOn = async (kind = 'turn-on') => {
    if (busy) return;
    setBusy(kind); setError(null); setInfo(null);
    const s = statusRef.current || status;
    const prefetched = { newTabUrl: isLivePick ? s?.newTabUrl : (picked?.newTabUrl || null), pairing: code ? { code: code.code } : s?.pairing };
    try {
      const data = await turnOnAiEditor({ themeId: targetId || null, prefetched });
      setStatus(st => (st ? { ...st, enabled: true } : st));
      if (data.code?.code) setCode({ code: data.code.code, display: data.code.display, expiresAt: data.code.expiresAt });
      if (!data.secretReady) setInfo('Shopify is still preparing the automatic connection for this store — if the panel asks for a code, paste the one below.');
      setTimeout(load, 1500);
    } catch (e) {
      if (e.status === 403 && e.code === 'feature_not_in_plan') { setLocked(true); return; }
      setError(e.message);
    } finally { setBusy(null); }
  };

  const disconnect = async () => {
    if (busy) return;
    setBusy('disconnect'); setError(null); setInfo(null);
    try {
      const res = await apiFetch('/api/theme-editor-admin/sessions/revoke', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || 'Could not disconnect.');
      setStatus(s => (s ? { ...s, enabled: false, connection: { ...(s.connection || {}), state: 'off' } } : s));
      setInfo('Disconnected. Every theme editor panel is signed out and every remembered browser forgotten; click “Turn on AI editor” to connect again.');
    } catch (e) { setError(e.message); } finally { setBusy(null); }
  };

  if (locked) {
    return (
      <div data-testid="theme-editor-ai-card" style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '18px 20px', borderRadius: 12, background: 'linear-gradient(135deg, rgba(107,78,232,0.07) 0%, rgba(74,144,226,0.07) 100%)', border: '1px solid #e0daf7' }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#2b2650' }}>🔒 Autonoma AI in the theme editor — Scale plan and up</div>
        <div style={{ fontSize: 13, color: '#6d6a85', maxWidth: 560 }}>Edit any section of any theme with AI right inside Shopify’s theme editor: click a section, say what to change, done.</div>
        <div><Button variant="primary" onClick={() => openPlansModal()}>Upgrade plan</Button></div>
      </div>
    );
  }

  const loading = !status && !error;
  const themeOptions = (themes || []).map(t => ({ label: `${t.name}${t.live ? ' (live)' : t.role && t.role !== 'unpublished' ? ` (${t.role})` : ''}`, value: String(t.id) }));

  return (
    <BlockStack gap="300">
      <div data-testid="theme-editor-ai-card">
        {!compact && (
          <Text as="p" tone="subdued" variant="bodySm">
            Turn on the Autonoma AI panel in Shopify’s theme editor. Click any section, block or exact element — on any theme,
            including ones Autonoma didn’t build — and tell the AI what to change: settings, custom CSS, whole new sections,
            section code, images, pages and menus. It works on the theme you have open, validates every code change with
            Shopify’s theme check and keeps an Undo for every change.
          </Text>
        )}
      </div>
      {error && <Banner tone="critical" onDismiss={() => setError(null)}><p>{error}</p></Banner>}
      {info && <Banner tone="info" onDismiss={() => setInfo(null)}><p>{info}</p></Banner>}

      {/* The connection card: one primary button, the theme picker, Disconnect. */}
      <div data-testid="theme-editor-embed-row" data-state={state} style={{ border: '1px solid #e4e0f7', borderRadius: 12, padding: '14px 16px', background: '#fbfaff' }}>
        <BlockStack gap="300">
          <InlineStack gap="200" blockAlign="center" wrap>
            <Text as="h3" variant="headingSm">AI theme editor</Text>
            {loading ? <Spinner size="small" accessibilityLabel="Loading" /> : <StateBadge state={state} />}
            {!loading && pickedLoading && <Spinner size="small" accessibilityLabel="Checking theme" />}
          </InlineStack>

          {!loading && state === 'connected' && (
            <BlockStack gap="100">
              <Text as="p" variant="bodyMd" fontWeight="semibold" data-testid="theme-editor-connected-line">
                Connected on {targetName || 'your theme'}
              </Text>
              {lastUsed && <Text as="p" tone="subdued" variant="bodySm">Last used {relativeTime(lastUsed)}</Text>}
            </BlockStack>
          )}
          {!loading && state === 'almost' && (
            <Text as="p" variant="bodyMd" fontWeight="semibold" data-testid="theme-editor-almost-line">Almost done — click Save in the theme editor</Text>
          )}
          {!loading && state === 'off' && !compact && (
            <Text as="p" tone="subdued" variant="bodySm">
              One click opens the theme editor in a new tab with the Autonoma AI embed switched on. Click Save there once — the
              panel connects by itself; if it asks for a code, paste the one shown here. It stays connected until you disconnect it.
            </Text>
          )}

          <InlineStack gap="200" blockAlign="center" wrap>
            {state === 'off' && (
              <Button variant="primary" onClick={() => turnOn('turn-on')} loading={busy === 'turn-on'} disabled={loading || pickedLoading || !!busy} data-testid="theme-editor-ai-enable">
                {TURN_ON_LABEL}
              </Button>
            )}
            {state !== 'off' && (
              <Button variant="primary" onClick={() => turnOn('open')} loading={busy === 'open'} disabled={loading || pickedLoading || !!busy} data-testid="theme-editor-ai-open">
                {OPEN_LABEL}
              </Button>
            )}
            {state === 'connected' && (
              <Button onClick={disconnect} loading={busy === 'disconnect'} disabled={!!busy && busy !== 'disconnect'} tone="critical" variant="secondary" data-testid="theme-editor-ai-revoke">
                Disconnect
              </Button>
            )}
            {themes && themes.length > 1 && (
              <div style={{ minWidth: 220 }} data-testid="theme-editor-theme-picker">
                <Select
                  label="Theme"
                  labelHidden
                  options={themeOptions}
                  value={targetId}
                  onChange={pickTheme}
                  disabled={!!busy}
                />
              </div>
            )}
          </InlineStack>
          {themes && themes.length > 1 && !isLivePick && (
            <Text as="p" tone="subdued" variant="bodySm">Connecting on “{targetName}” — a draft theme; your live theme is untouched until you publish it.</Text>
          )}

          {/* The connect code — always visible while the AI editor is switched on. */}
          {!loading && enabled && (
            <div data-testid="theme-editor-code-block" style={{ borderTop: '1px solid #eeecf7', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Text as="p" tone="subdued" variant="bodySm">
                Your connect code — if the panel in the editor asks for one, paste this. Codes work once and are valid for {status?.session?.codeMinutes || 10} minutes; a new one appears here automatically.
              </Text>
              {code ? (
                <ConnectCode code={code} onExpired={() => { setCode(null); fetchCode(); }} />
              ) : (
                <InlineStack gap="200" blockAlign="center">
                  <Spinner size="small" accessibilityLabel="Getting a new code" />
                  <Text as="span" tone="subdued" variant="bodySm">Getting a new code…</Text>
                  {!minting && <Button variant="plain" onClick={fetchCode} data-testid="theme-editor-ai-get-code">Retry</Button>}
                </InlineStack>
              )}
            </div>
          )}
        </BlockStack>
      </div>

      <SectionLibraryRow />

      <Text as="p" tone="subdued" variant="bodySm">
        In the editor you’ll see a floating <strong>Autonoma AI</strong> button in the preview — it lives there because Shopify
        doesn’t allow apps inside the editor’s own sidebar. Once connected it stays connected
        ({status?.session?.idleHours || 24} hours idle, {status?.session?.maxDays || 30} days max) and this browser is remembered for {status?.session?.rememberDays || 30} days, so you won’t need the code again.
      </Text>
    </BlockStack>
  );
}
